/**
 * BarcodeRenderer.jsx
 * Drop-in barcode and QR rendering — loads JsBarcode + QRCode.js from CDN.
 * No npm install required. Add this file to your src/ folder.
 *
 * Usage:
 *   import { Barcode, QRCode, BarcodeCard } from './BarcodeRenderer';
 *   <Barcode value="LINE000123" />
 *   <QRCode value="PRD000001" size={100} />
 *   <BarcodeCard label="Line A" value="LINE000001" type="barcode" />
 *
 * FIX (this version): the previous module-level callback-array pattern could
 * leave some <Barcode> instances permanently stuck on the "⟳ loading..."
 * placeholder even after the CDN script had successfully loaded — this
 * happened when many Barcode components mounted in the same render pass
 * (e.g. a grid of period/bundle barcodes) and the ready-state notification
 * raced against component mount order. Fixed by dispatching a real browser
 * event ('jsbarcode-ready' / 'qrcode-ready') that every mounted instance
 * listens for independently, instead of relying on a shared callback array
 * that could miss late-mounting components.
 */

import React, { useEffect, useRef, useState } from 'react';

// ─── CDN LOADER ───────────────────────────────────────────────────────────────
let jsBarcodeLoading = false;
let qrcodeLoading    = false;

function isJsBarcodeReady() { return typeof window !== 'undefined' && !!window.JsBarcode; }
function isQRCodeReady()    { return typeof window !== 'undefined' && !!window.QRCode; }

function loadScriptWithFallback(srcs, eventName, idx = 0) {
  if (idx >= srcs.length) {
    console.error('All CDN sources failed for', eventName);
    return;
  }
  const src = srcs[idx];
  if (document.querySelector(`script[src="${src}"]`)) {
    if (eventName === 'jsbarcode-ready' && isJsBarcodeReady()) window.dispatchEvent(new Event(eventName));
    if (eventName === 'qrcode-ready' && isQRCodeReady()) window.dispatchEvent(new Event(eventName));
    return;
  }
  const s = document.createElement('script');
  s.src = src; s.async = true;
  s.onload = () => window.dispatchEvent(new Event(eventName));
  s.onerror = () => {
    console.warn('CDN failed, trying next source:', src);
    s.remove();
    loadScriptWithFallback(srcs, eventName, idx + 1);
  };
  document.head.appendChild(s);
}

function ensureJsBarcode() {
  if (isJsBarcodeReady() || jsBarcodeLoading) return;
  jsBarcodeLoading = true;
  loadScriptWithFallback([
    'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js',
    'https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js',
  ], 'jsbarcode-ready');
}

function ensureQRCode() {
  if (isQRCodeReady() || qrcodeLoading) return;
  qrcodeLoading = true;
  // jsdelivr's /build/qrcode.min.js path can intermittently serve an
  // incorrect Content-Type (text/plain) on some edge nodes, which browsers
  // block under X-Content-Type-Options: nosniff. unpkg is used as the
  // primary source here since it reliably serves application/javascript;
  // jsdelivr is kept as a fallback in case unpkg itself is unreachable.
  loadScriptWithFallback([
    'https://unpkg.com/qrcode@1.5.3/build/qrcode.min.js',
    'https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js',
  ], 'qrcode-ready');
}

// ─── BARCODE COMPONENT (Code128) ─────────────────────────────────────────────
export function Barcode({
  value,
  width = 1.8,
  height = 50,
  fontSize = 11,
  displayValue = true,
  lineColor = '#111827',
}) {
  const ref = useRef(null);
  const [ready, setReady] = useState(isJsBarcodeReady());
  const [error, setError] = useState(null);

  // Every mounted instance independently listens for the ready event —
  // no shared callback array, so late-mounting components can't miss it.
  useEffect(() => {
    if (ready) return;
    const onReady = () => setReady(true);
    window.addEventListener('jsbarcode-ready', onReady);
    ensureJsBarcode();
    // Safety net: in case the event fired between our initial useState check
    // and this listener attaching, re-check directly.
    if (isJsBarcodeReady()) setReady(true);
    return () => window.removeEventListener('jsbarcode-ready', onReady);
  }, [ready]);

  useEffect(() => {
    if (!ready || !ref.current || !value) return;
    setError(null);
    try {
      window.JsBarcode(ref.current, String(value), {
        format: 'CODE128B',
        width, height, fontSize, displayValue, lineColor,
        background: 'transparent',
        margin: 4,
        textMargin: 3,
      });
    } catch (e) {
      setError(String(e.message || 'Invalid barcode'));
    }
  }, [ready, value, width, height, fontSize, displayValue, lineColor]);

  if (!value) return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  if (error)  return <span style={{ fontSize: 10, color: '#dc2626', fontFamily: 'monospace' }}>{value}</span>;
  if (!ready) return <span style={{ fontSize: 11, color: '#94a3b8' }}>⟳ loading...</span>;
  return <svg ref={ref} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />;
}

// ─── QR CODE COMPONENT ────────────────────────────────────────────────────────
export function QRCode({ value, size = 100, color = '#111827', bg = '#ffffff' }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(isQRCodeReady());

  useEffect(() => {
    if (ready) return;
    const onReady = () => setReady(true);
    window.addEventListener('qrcode-ready', onReady);
    ensureQRCode();
    if (isQRCodeReady()) setReady(true);
    return () => window.removeEventListener('qrcode-ready', onReady);
  }, [ready]);

  useEffect(() => {
    if (!ready || !ref.current || !value) return;
    window.QRCode.toCanvas(ref.current, String(value), {
      width: size, margin: 1,
      color: { dark: color, light: bg },
      errorCorrectionLevel: 'M',
    }).catch(e => console.error('QR error:', e));
  }, [ready, value, size, color, bg]);

  if (!value) return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  if (!ready) return (
    <div style={{ width: size, height: size, background: '#f1f5f9', borderRadius: 4,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>⟳</div>
  );
  return <canvas ref={ref} style={{ display: 'block', borderRadius: 4 }} />;
}

// ─── BARCODE CARD ─────────────────────────────────────────────────────────────
const cardStyle = (selected) => ({
  background: '#fff',
  border: `2px solid ${selected ? '#2563eb' : '#e2e8f0'}`,
  borderRadius: 8,
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  cursor: 'pointer',
  boxShadow: selected ? '0 0 0 3px rgba(37,99,235,.15)' : '0 1px 3px rgba(0,0,0,.06)',
  transition: 'all .15s',
  minWidth: 140,
  width: 140,
  userSelect: 'none',
});

export function BarcodeCard({ label, subLabel, value, type = 'barcode', selected, onClick }) {
  // Dynamic bar width: longer encoded values need thinner bars to fit the
  // same fixed-width card. JsBarcode's CODE128 auto-encoding adds ~11px of
  // fixed overhead (quiet zones) plus roughly 11px per character at width=1,
  // so we solve backwards from the available card width (140 - 24 padding
  // = ~116px usable) to pick a width multiplier that keeps any value legible
  // and contained instead of overflowing.
  const len = String(value || '').length;
  const usablePx = 116;
  const pxPerCharAtWidth1 = 11;
  const idealWidth = len > 0 ? usablePx / (len * pxPerCharAtWidth1) : 1.4;
  const barWidth = Math.max(0.5, Math.min(1.4, idealWidth));

  return (
    <div style={cardStyle(selected)} onClick={onClick}>
      {label && (
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
          letterSpacing: '.5px', textAlign: 'center', lineHeight: 1.2 }}>
          {label}
        </div>
      )}
      {type === 'qr'
        ? <QRCode value={value} size={90} />
        : <div style={{ width: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
            <Barcode value={value} height={38} fontSize={9} width={barWidth} />
          </div>
      }
      <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#334155',
        fontWeight: 600, textAlign: 'center', wordBreak: 'break-all' }}>
        {value}
      </div>
      {subLabel && (
        <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>{subLabel}</div>
      )}
    </div>
  );
}

// ─── PRINT AREA ───────────────────────────────────────────────────────────────
export function PrintSheet({ title, items }) {
  return (
    <div id="print-sheet" style={{ display: 'none' }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #print-sheet, #print-sheet * { visibility: visible; }
          #print-sheet { position: absolute; top: 0; left: 0; width: 100%; }
          .print-card { display: inline-block; border: 1px solid #000; padding: 8px;
            margin: 4px; text-align: center; page-break-inside: avoid; }
          .print-title { font-size: 10px; font-weight: bold; margin-bottom: 4px; }
          .print-val { font-size: 8px; font-family: monospace; margin-top: 3px; }
        }
      `}</style>
      <div style={{ padding: 16, fontFamily: 'Arial, sans-serif' }}>
        <h2 style={{ fontSize: 14, marginBottom: 12 }}>{title}</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((item, i) => (
            <div key={i} className="print-card">
              <div className="print-title">{item.label}</div>
              {item.type === 'qr'
                ? <QRCode value={item.value} size={80} />
                : <Barcode value={item.value} height={35} fontSize={8} width={1.2} />
              }
              <div className="print-val">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
